import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { formatDate } from "../../lib/utils";
import { toast } from "sonner";

const USAGE = [["users", "Users"], ["clients", "Clients"], ["projects", "Projects"], ["tickets", "Tickets"], ["open_tickets", "Open"]];
const PLAN_USAGE_KEYS = [["users", "Users"], ["projects", "Projects"], ["clients", "Clients"]];

function isDueSoon(dateStr) {
  if (!dateStr) return false;
  return new Date(dateStr) - Date.now() < 7 * 864e5;
}

export default function TenantDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [plans, setPlans] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [renewBusy, setRenewBusy] = useState(false);

  const load = () => {
    setErr(null);
    api.get(`/admin/companies/${id}`).then((r) => setD(r.data)).catch((e) => setErr(e.response?.status === 404 ? "Not found." : "Failed to load."));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); api.get("/admin/plans").then((r) => setPlans(r.data)).catch(() => {}); }, [id]);
  if (err) return <div className="surface-card p-8 text-center text-slate-500">{err}</div>;
  if (!d) return <div className="surface-card p-8 text-center text-slate-400">Loading…</div>;
  const c = d.company;
  const suspended = c.status === "suspended";

  const startEditName = () => { setNameInput(c.name); setEditingName(true); };
  const cancelEditName = () => setEditingName(false);
  const saveName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed) { toast.error("Name cannot be empty"); return; }
    setSavingName(true);
    try {
      await api.patch(`/admin/companies/${id}/details`, { name: trimmed });
      toast.success("Name updated");
      setEditingName(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update name");
    } finally {
      setSavingName(false);
    }
  };

  const toggle = async () => {
    const next = suspended ? "active" : "suspended";
    const reason = next === "suspended" ? (window.prompt("Reason for suspension (optional):") ?? "") : "";
    setBusy(true);
    try { await api.patch(`/admin/companies/${id}`, { status: next, reason: reason || undefined }); toast.success(`Tenant ${next}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const assignPlan = async (e) => {
    const plan_id = e.target.value || null;
    try { await api.put(`/admin/companies/${id}/plan`, { plan_id }); toast.success("Plan updated"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to update plan"); }
  };

  const markRenewed = async () => {
    setRenewBusy(true);
    try {
      await api.post(`/admin/companies/${id}/renew-plan`);
      toast.success("Renewed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to renew");
    } finally {
      setRenewBusy(false);
    }
  };

  const hasPlan = !!c.plan_id;
  const renewalDueSoon = hasPlan && isDueSoon(d.plan_renewal_at);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-blue-600 font-semibold">Tenant · {c.industry}</div>
          {editingName ? (
            <div className="flex items-center gap-2 mt-1">
              <input
                className="font-display text-2xl font-bold tracking-tight text-slate-900 border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") cancelEditName(); }}
                autoFocus
              />
              <Button size="sm" onClick={saveName} disabled={savingName}>Save</Button>
              <Button size="sm" variant="outline" onClick={cancelEditName} disabled={savingName}>Cancel</Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 mt-1">
              <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">{c.name}</h1>
              <button
                onClick={startEditName}
                className="text-slate-400 hover:text-slate-700 transition-colors p-1 rounded"
                title="Edit name"
                aria-label="Edit tenant name"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </button>
            </div>
          )}
          <p className="text-sm text-slate-500 mt-1">Status: <span className={suspended ? "text-rose-600 font-medium" : "text-emerald-600 font-medium"}>{c.status}</span> · Created {formatDate(c.created_at)}</p>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-sm text-slate-500">Plan:</span>
            <select
              value={c.plan_id || ""}
              onChange={assignPlan}
              className="text-sm border border-slate-200 rounded px-2 py-1 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">No plan</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          {hasPlan && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-slate-500">
              {d.plan_started_at && (
                <span>Active since <span className="text-slate-700 font-medium">{formatDate(d.plan_started_at)}</span></span>
              )}
              {d.plan_renewal_at && (
                <span>
                  Renews on{" "}
                  <span className={`font-medium ${renewalDueSoon ? "text-rose-600" : "text-slate-700"}`}>
                    {formatDate(d.plan_renewal_at)}
                  </span>
                </span>
              )}
              <Button size="sm" variant="outline" onClick={markRenewed} disabled={renewBusy}>
                Mark renewed
              </Button>
            </div>
          )}
        </div>
        <Button variant={suspended ? "default" : "destructive"} onClick={toggle} disabled={busy}>{suspended ? "Activate" : "Suspend"}</Button>
      </div>
      {d.limits_usage && (
        <div className="surface-card p-4">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold mb-3">Plan usage</div>
          <div className="grid grid-cols-3 gap-4">
            {PLAN_USAGE_KEYS.map(([key, label]) => {
              const u = d.limits_usage[key];
              if (!u) return null;
              const over = u.limit != null && u.used >= u.limit;
              return (
                <div key={key}>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">{label}</div>
                  <div className={`mt-1 font-display text-2xl font-bold tabular-nums ${over ? "text-rose-600" : "text-slate-900"}`}>
                    {u.used} / {u.limit != null ? u.limit : "∞"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {USAGE.map(([k, label]) => (
          <div key={k} className="surface-card p-5"><div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">{label}</div><div className="mt-2 font-display text-3xl font-bold tabular-nums">{d.usage[k] ?? 0}</div></div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="surface-card p-4">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold mb-2">Admin contacts</div>
          <div className="divide-y divide-slate-100">
            {d.super_admins.map((u, i) => (<div key={i} className="py-2"><div className="text-slate-800">{u.name}</div><div className="text-xs text-slate-500">{u.email}</div></div>))}
            {d.super_admins.length === 0 && <div className="py-3 text-xs text-slate-400">No admins.</div>}
          </div>
        </div>
        <div className="surface-card p-4">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold mb-2">Status history</div>
          <div className="divide-y divide-slate-100">
            {(d.status_history || []).slice().reverse().map((h, i) => (
              <div key={i} className="py-2 text-sm"><span className={h.status === "suspended" ? "text-rose-600" : "text-emerald-600"}>{h.status}</span> <span className="text-slate-400 text-xs">· {h.by_name} · {formatDate(h.at)}</span>{h.reason ? <div className="text-xs text-slate-500">{h.reason}</div> : null}</div>
            ))}
            {(d.status_history || []).length === 0 && <div className="py-3 text-xs text-slate-400">No changes yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
